import { Box, Button, Grid, Tooltip, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import ButtonWithFeddback from '../../../../components/ButtonWithFeddback';
import useWidth from '../../../../components/Hooks/useWidth';
import { TeceoModal } from '../../../../components/Modal/TeceoModal';
import LangEnum from '../../../../lang/LangEnum';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { validateOrderConditionsQuery } from '../../contexts/order.utils';
import ContractConfirmPersonalInfo from '../ContractConfirmPersonalInfo';
import { ContractHistory } from '../ContractHistory/ContractHistoryList';
import ContractPageWrapper from '../ContractPageWrapper';
import ContractSigned from '../ContractSigned';
import FullWidthBoxWrapper from '../FullWidthBoxWrapper';
import MethodOfSigning from '../MethodOfSigning';
import OrderContractModalFooter from '../OrderContractModalFooter';
import { ReadContract } from '../ReadContract/ReadContract';
import { SignContract } from '../TabPanel/SignContract';
import { SignatureContextProvider, useSignatureContext } from '../contexts/signature.context';
import { SignatureContextType } from '../contexts/signature.context.types';
import useGenerateContract from '../hooks/useGenerateContract';
import { ModalContractPage, ModalContractReadPage } from '../types';
import { getModalFooterSize } from '../utils';
import { useStyles } from './orderContractModal.styles';
import {
  BaseContractModalProps,
  CommonButtonProps,
  ContractModalProps,
  ModalCallToActionProps,
  ModalPageProps,
} from './orderContractModal.types';

const ModalPage = ({ orderId }: ModalPageProps): JSX.Element => {
  const { page } = useSignatureContext();
  switch (true) {
    case page === ModalContractPage.CONTRACT_HISTORY:
      return (
        <ContractPageWrapper>
          <FullWidthBoxWrapper>
            <ContractHistory orderId={orderId} />
          </FullWidthBoxWrapper>
        </ContractPageWrapper>
      );
    case ModalContractReadPage.includes(page):
      return <ReadContract orderId={orderId} />;
    case page === ModalContractPage.CREATE_SIGN_IMAGE:
      return (
        <ContractPageWrapper>
          <FullWidthBoxWrapper isCreateSignImage>
            <SignContract orderId={orderId} />
          </FullWidthBoxWrapper>
        </ContractPageWrapper>
      );
    case page === ModalContractPage.METHOD_OF_SIGNING:
      return (
        <ContractPageWrapper>
          <FullWidthBoxWrapper>
            <MethodOfSigning />
          </FullWidthBoxWrapper>
        </ContractPageWrapper>
      );
    case page === ModalContractPage.CONFIRM_PERSONAL_INFO:
      return (
        <ContractPageWrapper>
          <FullWidthBoxWrapper>
            <ContractConfirmPersonalInfo />
          </FullWidthBoxWrapper>
        </ContractPageWrapper>
      );
    case page === ModalContractPage.CONTRACT_SIGNED:
      return <ContractSigned />;
    default:
      return <div />;
  }
};

const ModalGoBack = () => {
  const { translate } = useTranslation();
  const { page, changePage, loadContractUrl, changeContractInfo } = useSignatureContext();
  const { smallWidth } = useWidth();
  const { classes } = useStyles();
  const buttonProps: CommonButtonProps = smallWidth
    ? { fullWidth: true, size: 'small' }
    : undefined;

  switch (true) {
    case page === ModalContractPage.READ_AND_SIGN_CONTRACT:
    case page === ModalContractPage.READ_AND_SIGN_NEW_CONTRACT:
      return (
        <Grid
          size={{
            xs: 12,
            sm: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: '180px',
            }}
          >
            <Button
              {...buttonProps}
              fullWidth
              onClick={() => {
                changePage(ModalContractPage.METHOD_OF_SIGNING);
              }}
              className={classes.button}
              variant="outlined"
            >
              {translate(LangEnum.GLOBAL_GO_BACK)}
            </Button>
          </Box>
        </Grid>
      );
    case ModalContractReadPage.includes(page):
      return (
        <Grid
          size={{
            xs: 12,
            sm: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: '180px',
            }}
          >
            <Button
              {...buttonProps}
              fullWidth
              onClick={() => {
                loadContractUrl(null);
                changePage(ModalContractPage.CONTRACT_HISTORY);
              }}
              className={classes.button}
              variant="outlined"
            >
              {translate(LangEnum.GLOBAL_GO_BACK)}
            </Button>
          </Box>
        </Grid>
      );
    case page === ModalContractPage.METHOD_OF_SIGNING:
      return (
        <Grid
          size={{
            xs: 12,
            sm: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: '180px',
            }}
          >
            <Button
              {...buttonProps}
              fullWidth
              onClick={() => {
                loadContractUrl(null);
                changeContractInfo(null);
                changePage(ModalContractPage.CONTRACT_HISTORY);
              }}
              className={classes.button}
              variant="outlined"
            >
              {translate(LangEnum.GLOBAL_GO_BACK)}
            </Button>
          </Box>
        </Grid>
      );
    case page === ModalContractPage.CREATE_SIGN_IMAGE:
      return (
        <Grid
          size={{
            xs: 12,
            sm: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: '180px',
            }}
          >
            <Button
              {...buttonProps}
              fullWidth
              onClick={() => {
                changePage(ModalContractPage.CONFIRM_PERSONAL_INFO);
              }}
              className={classes.button}
              variant="outlined"
            >
              {translate(LangEnum.GLOBAL_GO_BACK)}
            </Button>
          </Box>
        </Grid>
      );
    case page === ModalContractPage.CONFIRM_PERSONAL_INFO:
      return (
        <Grid
          size={{
            xs: 12,
            sm: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: '180px',
            }}
          >
            <Button
              {...buttonProps}
              fullWidth
              onClick={() => {
                changePage(ModalContractPage.READ_AND_SIGN_CONTRACT);
              }}
              className={classes.button}
              variant="outlined"
            >
              {translate(LangEnum.GLOBAL_GO_BACK)}
            </Button>
          </Box>
        </Grid>
      );
    default:
      return null;
  }
};

const ModalCallToAction = ({ orderId }: ModalCallToActionProps) => {
  const { translate } = useTranslation();
  const { classes } = useStyles();
  const {
    page,
    changePage,
    handleGenerateSignedContractPdf,
    isLoading,
    hasSignInput,
    personalInfo,
    isReadOnly,
  } = useSignatureContext();
  const { isStore, team } = useApplicationContext();
  const queryClient = useQueryClient();
  const { generate, isGenerating } = useGenerateContract(orderId);

  const ConfirmPersonalInfoButton = () => (
    <Button
      className={classes.button}
      fullWidth
      variant="contained"
      color="primary"
      disabled={!personalInfo}
      onClick={() => changePage(ModalContractPage.CREATE_SIGN_IMAGE)}
    >
      <Typography>{translate(LangEnum.ORDER_CONTRACT_CALL_TO_ACTION_CONTINUE)}</Typography>
    </Button>
  );

  const handleCreateNewContract = async () => {
    await generate();
    await queryClient.refetchQueries(validateOrderConditionsQuery(orderId, team?.id));
  };

  switch (true) {
    case page === ModalContractPage.CONTRACT_HISTORY && !isStore && !isReadOnly:
      return (
        <Grid
          size={{
            xs: 12,
            sm: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: '180px',
            }}
          >
            <ButtonWithFeddback
              fullWidth
              variant="contained"
              iconVariant="inherit"
              color="primary"
              status={isGenerating ? 'loading' : 'normal'}
              disabled={isGenerating}
              onClick={handleCreateNewContract}
            >
              <Typography>
                {translate(LangEnum.ORDER_CONTRACT_CALL_TO_ACTION_NEW_CONTRACT)}
              </Typography>
            </ButtonWithFeddback>
          </Box>
        </Grid>
      );
    case page === ModalContractPage.READ_AND_SIGN_NEW_CONTRACT:
    case page === ModalContractPage.READ_AND_SIGN_CONTRACT:
      return (
        <Grid
          size={{
            xs: 12,
            sm: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: '180px',
            }}
          >
            <Button
              className={classes.button}
              fullWidth
              variant="contained"
              color="primary"
              disabled={isLoading}
              onClick={() => {
                changePage(ModalContractPage.CONFIRM_PERSONAL_INFO);
              }}
            >
              <Typography>{translate(LangEnum.ORDER_CONTRACT_CALL_TO_ACTION_CONTINUE)}</Typography>
            </Button>
          </Box>
        </Grid>
      );
    case page === ModalContractPage.CREATE_SIGN_IMAGE:
      return (
        <Grid
          size={{
            xs: 12,
            sm: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: '180px',
            }}
          >
            <ButtonWithFeddback
              fullWidth
              variant="contained"
              color="primary"
              iconVariant="inherit"
              status={isLoading ? 'loading' : 'normal'}
              disabled={isLoading || !hasSignInput}
              onClick={async () => {
                await handleGenerateSignedContractPdf(orderId, personalInfo);
              }}
            >
              <Typography>{translate(LangEnum.ORDER_CONTRACT_CALL_TO_ACTION_APPLY)}</Typography>
            </ButtonWithFeddback>
          </Box>
        </Grid>
      );
    case page === ModalContractPage.CONFIRM_PERSONAL_INFO:
      if (!personalInfo) {
        <Grid
          size={{
            xs: 12,
            sm: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: '180px',
            }}
          >
            <Tooltip
              title={
                !personalInfo &&
                translate(LangEnum.ORDER_CONTRACT_CONFIRM_PERSONAL_INFO_INPUT_ALL_FIELDS)
              }
            >
              <span>
                <ConfirmPersonalInfoButton />
              </span>
            </Tooltip>
            ;
          </Box>
          ;
        </Grid>;
      }

      return (
        <Grid
          size={{
            xs: 12,
            sm: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              minWidth: '180px',
            }}
          >
            <ConfirmPersonalInfoButton />
          </Box>
        </Grid>
      );
    default:
      return null;
  }
};

const getModalTitle = (page: SignatureContextType.Page) => {
  switch (true) {
    case page === ModalContractPage.CONTRACT_HISTORY:
      return LangEnum.ORDER_CONTRACT_DIALOG_TITLE;
    case ModalContractReadPage.includes(page):
      return LangEnum.ORDER_CONTRACT_DIALOG_VIEW_CONTRACT_TITLE;
    case page === ModalContractPage.METHOD_OF_SIGNING:
      return LangEnum.ORDER_CONTRACT_DIALOG_METHOD_OF_SIGNING_TITLE;
    case page === ModalContractPage.CONFIRM_PERSONAL_INFO:
      return LangEnum.ORDER_CONTRACT_DIALOG_SIGN_CONTRACT_TITLE;
    case page === ModalContractPage.CREATE_SIGN_IMAGE:
      return LangEnum.ORDER_CONTRACT_DIALOG_SIGN_CONTRACT_TITLE;
    default:
      return LangEnum.ORDER_CONTRACT_DIALOG_TITLE;
  }
};

const BaseContractModal = ({ open, onClose, ...props }: BaseContractModalProps) => {
  const { loadInitState, page, unauthenticatedSign, selectedSignedContract } =
    useSignatureContext();

  const { mediumDownWidth, smallWidth, mobileWidth } = useWidth();
  const { translate } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();
  const needDisableDialogPaddingAndFooter =
    page === ModalContractPage.CONTRACT_HISTORY ||
    page === ModalContractPage.CREATE_NEW_CONTRACT ||
    page === ModalContractPage.CREATE_SIGN_IMAGE ||
    page === ModalContractPage.METHOD_OF_SIGNING ||
    page === ModalContractPage.CONFIRM_PERSONAL_INFO;

  const disableTitle = page === ModalContractPage.CONTRACT_SIGNED;
  const disableFooter =
    page === ModalContractPage.CONTRACT_SIGNED ||
    page === ModalContractPage.READ_CONTRACT_WITHOUT_FOOTER;
  const disableCloseButton =
    page === ModalContractPage.CONTRACT_SIGNED ||
    page === ModalContractPage.READ_CONTRACT_WITHOUT_FOOTER ||
    unauthenticatedSign;

  const needDisableOnlyDialogPadding = ModalContractReadPage.includes(page);

  const doesntRenderInfo =
    mobileWidth &&
    (ModalContractReadPage.includes(page) ||
      ModalContractPage.CONFIRM_PERSONAL_INFO === page ||
      ModalContractPage.CREATE_SIGN_IMAGE === page);

  const hasTitleDivider = ModalContractReadPage.includes(page);

  const orderId = selectedSignedContract?.order?.id || props.orderId;

  const handleOnClose = () => {
    if (onClose) {
      onClose();
      loadInitState();
    }
  };

  return (
    <TeceoModal
      disableTitleDivider={!hasTitleDivider}
      disableCloseButton={disableCloseButton}
      open={open}
      onClose={handleOnClose}
      title={disableTitle ? '' : translate(getModalTitle(page))}
      disableDialogContentPadding={
        needDisableDialogPaddingAndFooter || needDisableOnlyDialogPadding
      }
      dialogTitleStyle={{
        paddingBottom: smallWidth && !needDisableOnlyDialogPadding ? theme.spacing(2) : undefined,
      }}
      disableDialogActionsPadding
      dialogProps={{
        maxWidth: 'lg',
        fullWidth: true,
        fullScreen: mediumDownWidth,
        classes: {
          paper: classes.modalRoot,
        },
        disableScrollLock: true,
      }}
      titleProps={{
        variant: 'h5',
        align: 'left',
        fontWeight: 600,
        style: {
          textTransform: 'lowercase',
          fontSize: smallWidth ? 20 : undefined,
        },
      }}
      dialogActions={
        disableFooter ? undefined : (
          <Box
            style={
              needDisableDialogPaddingAndFooter || ModalContractReadPage.includes(page)
                ? {
                    borderTop: '1px solid rgba(0,0,0, 0.16)',
                  }
                : {}
            }
            sx={{
              width: '100%',
              height: getModalFooterSize(smallWidth),
              display: 'flex',
              px: 3,
              alignItems: 'center',
            }}
          >
            <Grid
              container
              spacing={1}
              sx={{
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              {!doesntRenderInfo && (
                <Grid size="grow">
                  <OrderContractModalFooter />
                </Grid>
              )}
              <Grid
                size={{
                  xs: 12,
                  sm: 'auto',
                }}
              >
                <Grid
                  container
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <ModalGoBack />
                  <ModalCallToAction orderId={orderId!} />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )
      }
    >
      <ModalPage orderId={orderId!} />
    </TeceoModal>
  );
};

export const ContractModal = ({ disableContext, isReadOnly, ...props }: ContractModalProps) => {
  if (disableContext) {
    return <BaseContractModal {...props} />;
  }

  return (
    <SignatureContextProvider isReadOnly={isReadOnly}>
      <BaseContractModal {...props} />
    </SignatureContextProvider>
  );
};
